import { Component, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { I18nService, Lang } from './i18n.service';

/**
 * English / اردو toggle.
 *
 * Two languages, so this is a straight toggle rather than a dropdown: a menu that opens to reveal two
 * options costs an extra tap to do what one tap can. If a third language is ever added this has to
 * become a list — the `variant` split already isolates that change to this file.
 *
 * Each option is labelled in ITS OWN script ("English", "اردو"), never translated. Someone who reads
 * only Urdu needs to find اردو on a screen they cannot otherwise read, and "Urdu" in Latin letters is
 * exactly what they cannot read.
 */
@Component({
  selector: 'app-lang-switcher',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div [class]="variant() === 'drawer' ? 'ls-row' : 'ls-pill'"
         role="group" [attr.aria-label]="i18n.t('nav.chooseLanguage')">
      @if (variant() === 'drawer') {
        <span class="ls-row__label">
          <span class="ls-row__ico" aria-hidden="true">🌐</span>
          <span class="ls-row__text">{{ i18n.t('nav.language') }}</span>
        </span>
      }
      <div class="ls-opts">
        @for (o of i18n.options; track o.code) {
          <button
            type="button"
            class="ls-opt"
            [class.is-on]="i18n.lang() === o.code"
            [attr.aria-pressed]="i18n.lang() === o.code"
            [attr.lang]="o.code"
            (click)="choose(o.code, $event)">{{ o.nativeName }}</button>
        }
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    /* Header placement — a translucent segmented control on the red bar, matching the install pill. */
    .ls-pill {
      display: inline-flex; align-items: center; flex: 0 0 auto;
      border: 1px solid rgba(255, 255, 255, 0.45); background: rgba(255, 255, 255, 0.12);
      border-radius: 999px; padding: 2px;
    }
    .ls-pill .ls-opt {
      border: none; background: transparent; cursor: pointer; font: inherit;
      font-size: 0.8rem; font-weight: 700; color: #fff;
      padding: 0.28rem 0.62rem; border-radius: 999px; white-space: nowrap;
    }
    .ls-pill .ls-opt.is-on { background: #fff; color: var(--color-primary, #FA0E0E); }

    /* Drawer placement — a labelled row matching .oy-drawer__links a. Wraps for the same reason the
       text-size row does: label + fixed control can exceed a 300px drawer, and wrapping the ROW is
       better than the label breaking inside its own cell. */
    .ls-row {
      display: flex; align-items: center; justify-content: space-between;
      gap: 0.6rem; row-gap: 0.4rem; flex-wrap: wrap;
      width: 100%; padding: 0.75rem 0.6rem;
    }
    /* 1.5rem icon box, shared with .oy-drawer__links a so every drawer label starts on the same line. */
    .ls-row__label {
      display: flex; align-items: center; gap: 0.7rem; min-width: 0;
      font-size: 1rem; font-weight: 600; color: var(--color-text, #17120f);
    }
    .ls-row__ico { flex: 0 0 1.5rem; text-align: center; font-size: 1rem; line-height: 1; }
    .ls-row__text { min-width: 0; }
    .ls-row .ls-opts { display: inline-flex; border: 1px solid #ece3e3; border-radius: 999px; padding: 2px; }
    .ls-row .ls-opt {
      border: none; background: transparent; cursor: pointer; font: inherit;
      font-size: 0.85rem; font-weight: 700; color: var(--color-text-muted, #6b6b6b);
      padding: 0.3rem 0.7rem; border-radius: 999px;
    }
    .ls-row .ls-opt.is-on { background: var(--color-primary, #FA0E0E); color: #fff; }

    .ls-opts { display: inline-flex; }

    /* The Urdu option renders in an Urdu face even while the UI is English, so it is legible to the
       person looking for it. */
    .ls-opt[lang="ur"] { font-family: var(--oy-font-urdu); font-size: 0.95em; }

    /* Below 1080px the header hands its nav to the drawer, where the row version takes over.
       MUST match ordering-layout.css's header breakpoint — if this is wider, the pill shows in the
       header AND in the drawer at the same time. Moved 860 → 1080 on 2026-09-22 with the layout. */
    @media (max-width: 1080px) {
      .ls-pill { display: none; }
    }
  `],
})
export class LangSwitcherComponent {
  readonly variant = input<'header' | 'drawer'>('header');

  /** Raised after a choice, so the drawer can close itself behind it. */
  readonly changed = output<void>();

  protected readonly i18n = inject(I18nService);

  choose(lang: Lang, event: MouseEvent): void {
    event.stopPropagation();
    if (this.i18n.lang() !== lang) this.i18n.set(lang);
    this.changed.emit();
  }
}
